/**
 * v6.38.0 (G-PG-3) — Parity Governance Guardrail React hook.
 *
 * Fetches GET /api/bp-auditor/diagnostics/parity_governance once per
 * mount (super-admin gated). Caches in a tiny module-level cache so
 * multiple <ParityNumber> instances on the same page share one request.
 *
 * GRACEFUL DEGRADE:
 *   - Non-super-admin (401/403)  → returns isEnabled=false (UI behaves
 *     exactly as today: numbers render normally).
 *   - Network error / endpoint missing → returns isEnabled=false.
 *   - Backend feature flag OFF (overall: DISABLED) → returns
 *     isEnabled=false. UI behaves exactly as today.
 *
 * Only when isEnabled=true AND overall=RED do components withhold
 * unbacked numbers. This is the structural enforcement.
 *
 * PLAN: /app/memory/PARITY_GOVERNANCE_GUARDRAIL_PLAN_v1.md §5.1
 */
import { useEffect, useState } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ENDPOINT = `${API}/bp-auditor/diagnostics/parity_governance`;

let _cache = null;       // last successful report (shared across mounts)
let _cacheAt = 0;
const _CACHE_TTL_MS = 60_000;  // 60s — short enough that flag flips show up quickly

let _inflight = null;    // dedupe concurrent requests

async function _fetchReport() {
  if (_inflight) return _inflight;
  _inflight = (async () => {
    try {
      const { data } = await axios.get(ENDPOINT, { timeout: 8000 });
      _cache = data;
      _cacheAt = Date.now();
      return data;
    } catch (e) {
      // 401/403/404/5xx all degrade gracefully.
      _cache = { overall: 'DISABLED', _degraded: true, _error: e?.response?.status || 'network' };
      _cacheAt = Date.now();
      return _cache;
    } finally {
      _inflight = null;
    }
  })();
  return _inflight;
}

/**
 * Hook returning the live governance report.
 *
 * @returns {{
 *   report: object | null,
 *   loading: boolean,
 *   isEnabled: boolean,    // true iff backend reports an actionable GREEN/AMBER/RED
 *   overall: 'GREEN'|'AMBER'|'RED'|'DISABLED'|'UNKNOWN',
 *   refresh: () => Promise<void>,
 * }}
 */
export default function useParityGovernance() {
  const _initial = (() => {
    if (_cache && (Date.now() - _cacheAt) < _CACHE_TTL_MS) return _cache;
    return null;
  });
  const [report, setReport] = useState(_initial);
  const [loading, setLoading] = useState(() => !(
    _cache && (Date.now() - _cacheAt) < _CACHE_TTL_MS));

  useEffect(() => {
    if (_cache && (Date.now() - _cacheAt) < _CACHE_TTL_MS) {
      // Cache fresh — initial state already used it; nothing to do.
      return;
    }
    let cancelled = false;
    _fetchReport().then(r => {
      if (!cancelled) {
        setReport(r);
        setLoading(false);
      }
    });
    return () => { cancelled = true; };
  }, []);

  const refresh = async () => {
    setLoading(true);
    _cache = null;
    const r = await _fetchReport();
    setReport(r);
    setLoading(false);
  };

  const overall = (report && report.overall) || 'UNKNOWN';
  // isEnabled = the guardrail is actively reporting something actionable.
  // DISABLED / degraded both report isEnabled=false → UI shows nothing new.
  const isEnabled = !!report
    && !report._degraded
    && (overall === 'GREEN' || overall === 'AMBER' || overall === 'RED');

  return { report, loading, isEnabled, overall, refresh };
}
